import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import {
    ClickAwayListener,
    Paper,
    Popper,
    Radio,
    RadioGroup,
    FormControlLabel,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { getConfig } from '@klpkonsern/appconfig';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { Bruker } from '@klp/user';
import { setDevRoleOverride, clearDevRoleOverride } from '@klp/user/userSlice';
import type { DevRolePreset } from './devRolePresets';
import { DEV_ROLE_PRESETS, DEV_ROLE_STORAGE_KEY } from './devRolePresets';

const ROLE_FLAG_LABELS: Partial<Record<keyof Bruker, string>> = {
    isForvalter: 'Forvalter',
    isKontorForvalter: 'Kontorforvalter',
    isHandelForvalter: 'Handelforvalter',
    isQAOkonomi: 'QA Økonomi',
    isProsjekteier: 'Prosjekteier',
    isProsjektleder: 'Prosjektleder',
    isProsjektdirektor: 'Prosjektdirektør',
    isProsjektkontroller: 'Prosjektkontroller',
    isForvaltningsdirektor: 'Forvaltningsdirektør',
    isUtviklingsdirektor: 'Utviklingsdirektør',
    isAdministrerendedirektor: 'Administrerende direktør',
    isOkonomisjef: 'Økonomisjef',
    isTekniskSjef: 'Teknisk sjef',
    isOkonomi: 'Økonomi',
    isDrift: 'Drift',
    isLeverandorAnsvarlig: 'Leverandøransvarlig',
    isBrukerAdministrator: 'Brukeradministrator',
    isAdmin: 'Admin',
    isEDAdmin: 'ED Admin',
};

const getActiveRoleLabels = (preset: DevRolePreset): Array<string> => {
    if (!preset.overrides) return [];
    return (Object.keys(ROLE_FLAG_LABELS) as Array<keyof Bruker>)
        .filter((key) => preset.overrides?.[key] === true)
        .map((key) => ROLE_FLAG_LABELS[key]!);
};

const styles = (theme: Theme) => ({
    trigger: {
        position: 'fixed' as const,
        bottom: theme.spacing(2),
        left: theme.spacing(2),
        zIndex: 10000,
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
        fontSize: '0.75rem',
        fontWeight: 700,
        backgroundColor: '#FFC946',
        color: theme.palette.primary.main,
        border: 'none',
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        boxShadow: theme.shadows[2],
        '&:hover': {
            backgroundColor: '#FFB800',
        },
    },
    popper: {
        zIndex: 10001,
    },
    paper: {
        padding: theme.spacing(2),
        minWidth: 220,
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.08em',
    },
    label: {
        fontSize: '0.875rem',
    },
});

export const DevRoleSelector = () => {
    const environmentType = getConfig('WEBAPP_ENVIRONMENT_TYPE_DISPLAY', '');
    if (!environmentType) return null;

    return <DevRoleSelectorInner />;
};

const DevRoleSelectorInner = () => {
    const classes = useKlpStyles({}, styles);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [activePresetId, setActivePresetId] = useState('original');
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const storedId = localStorage.getItem(DEV_ROLE_STORAGE_KEY);
        const preset = DEV_ROLE_PRESETS.find((p) => p.id === storedId);
        if (preset?.overrides) {
            dispatch(setDevRoleOverride(preset.overrides));
            setActivePresetId(preset.id);
        }
    }, [dispatch]);

    const activePreset =
        DEV_ROLE_PRESETS.find((p) => p.id === activePresetId) ??
        DEV_ROLE_PRESETS[0];

    const handleChange = (
        _: React.ChangeEvent<HTMLInputElement>,
        value: string,
    ) => {
        const preset = DEV_ROLE_PRESETS.find((p) => p.id === value);
        if (!preset) return;

        if (preset.overrides === null) {
            dispatch(clearDevRoleOverride());
            localStorage.removeItem(DEV_ROLE_STORAGE_KEY);
        } else {
            dispatch(setDevRoleOverride(preset.overrides));
            localStorage.setItem(DEV_ROLE_STORAGE_KEY, preset.id);
        }
        setActivePresetId(preset.id);
        setOpen(false);
    };

    return (
        <>
            <button
                ref={anchorRef}
                className={classes.trigger}
                onClick={() => setOpen((prev) => !prev)}
            >
                {t('devRoleSelector.label')}: {t(activePreset.labelKey)}
            </button>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="top-start"
                className={classes.popper}
            >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Paper className={classes.paper} elevation={4}>
                        <Typography className={classes.title}>
                            {t('devRoleSelector.heading')}
                        </Typography>
                        <RadioGroup
                            value={activePreset.id}
                            onChange={handleChange}
                        >
                            {DEV_ROLE_PRESETS.map((preset) => {
                                const roleLabels = getActiveRoleLabels(preset);
                                return (
                                    <Tooltip
                                        key={preset.id}
                                        placement="right"
                                        title={
                                            roleLabels.length > 0 ? (
                                                <>
                                                    {roleLabels.map((l) => (
                                                        <div key={l}>{l}</div>
                                                    ))}
                                                </>
                                            ) : (
                                                ''
                                            )
                                        }
                                    >
                                        <FormControlLabel
                                            value={preset.id}
                                            control={
                                                <Radio
                                                    size="small"
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <span className={classes.label}>
                                                    {t(preset.labelKey)}
                                                </span>
                                            }
                                        />
                                    </Tooltip>
                                );
                            })}
                        </RadioGroup>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};
